import React, {useState, useCallback} from 'react';
import {LayoutChangeEvent, Pressable, Platform} from 'react-native';
import styled, {css, useTheme} from 'styled-components/native';
import Animated, {FadeInDown, FadeOutDown} from 'react-native-reanimated';
import TextGeneric from '../textGeneric';
import Icon from '../icon';
import useResponsiveDimensions from '../../utils/hooks/useResponsiveDimensions';
import {isWeb} from '../../utils/responsive';

const TipContainer = styled.View<{
  containerHeight: number;
  wdp: (v: number) => number;
  hdp: (v: number) => number;
  getByScreenSize: (mobile: string, desktop: string) => string;
}>`
  max-width: ${({getByScreenSize}) => getByScreenSize('85%', '700px')};
  min-width: ${({getByScreenSize}) => getByScreenSize('70%', '500px')};
  background-color: ${({theme}) => theme.cardBackground};
  border-radius: 12px;
  align-self: flex-end;
  padding-vertical: 12px;
  position: absolute;
  top: -${({containerHeight, theme, wdp, hdp}) =>
      containerHeight + theme.text.s7 * 3 + hdp(1) + wdp(5)}px;
  right: 0;
  z-index: 99;
  ${isWeb
    ? css`
        box-shadow: 0px 4px 12px ${({theme}) => `${theme.text.dark}20`};
      `
    : css`
        shadow-color: ${({theme}) => theme.text.dark};
        shadow-opacity: 0.2;
        shadow-radius: 8px;
        shadow-offset: 0px 4px;
        elevation: 8;
      `}
`;

const TipText = styled(TextGeneric)<{responsivePadding: string}>`
  font-size: ${({theme}) => theme.text.s9}px;
  line-height: ${({theme}) => theme.text.s9 * 1.5}px;
  color: ${({theme}) => theme.text.dark};
  padding: ${({responsivePadding}) => responsivePadding};
  padding-top: ${({theme}) => theme.text.s55}px;
  padding-bottom: ${({theme}) => theme.text.s12}px;
`;

const CloseIconContainer = styled.TouchableOpacity`
  position: absolute;
  top: ${({theme}) => theme.text.s10}px;
  right: ${({theme}) => theme.text.s10}px;
  z-index: 100;
  width: ${({theme}) => theme.text.s55 * 1.5}px;
  height: ${({theme}) => theme.text.s55 * 1.5}px;
  align-items: center;
  justify-content: center;
  border-radius: ${({theme}) => theme.text.s55 * 0.75}px;
  background-color: ${({theme}) => theme.ripple + '40'};
  ${Platform.OS === 'web' &&
    css`
      transition: background-color 0.2s ease;
      &:hover {
        background-color: ${({theme}) => theme.ripple + '80'};
      }
    `}
`;

const Triangle = styled.View<{wdp: (v: number) => number}>`
  width: 0;
  height: 0;
  background-color: transparent;
  border-style: solid;
  border-left-width: ${({wdp}) => wdp(3.5)}px;
  border-right-width: ${({wdp}) => wdp(3.5)}px;
  border-top-width: ${({wdp}) => wdp(3.5)}px;
  border-left-color: transparent;
  border-right-color: transparent;
  border-top-color: ${({theme}) => theme.cardBackground};
  position: absolute;
  right: ${({wdp}) => wdp(3.5)}px;
  bottom: -${({wdp}) => wdp(3.4)}px;
`;

const DismissOverlay = styled(Pressable)<{
  wdp: (v: number) => number;
  hdp: (v: number) => number;
}>`
  width: ${({wdp}) => wdp(100)}px;
  height: ${({hdp}) => hdp(100)}px;
  position: absolute;
  transform: scale(10);
  z-index: 2;
`;

interface TibButtonProps {
  isVisible: boolean;
  onDismiss: () => void;
  tipText: string;
}

export const TibButton: React.FC<TibButtonProps> = ({
  isVisible,
  onDismiss,
  tipText,
}) => {
  const theme = useTheme();
  const {wdp, hdp, getByScreenSize} = useResponsiveDimensions();
  const [height, setHeight] = useState(0);

  const handleLayout = useCallback((e: LayoutChangeEvent) => {
    setHeight(e.nativeEvent.layout.height);
  }, []);

  // Split text by double newlines and clean up whitespace
  const formattedText = tipText
  .trim() // Trim the entire string first
  .split('\n\n')
  .map(line => line.trim())
  .filter(line => line.length > 0)
  .join('\n\n');

  if (!isVisible) return null;

  return (
    <>
      <DismissOverlay wdp={wdp} hdp={hdp} onPressIn={onDismiss} />

      <Animated.View
        style={{zIndex: 3}}
        entering={FadeInDown}
        exiting={FadeOutDown}>
        <TipContainer
          onLayout={handleLayout}
          containerHeight={height}
          wdp={wdp}
          hdp={hdp}
          getByScreenSize={getByScreenSize}>
          <CloseIconContainer onPress={onDismiss}>
            <Icon name="x" size={theme.text.s55} color={theme.text.grey} />
          </CloseIconContainer>

          <TipText
            responsivePadding={getByScreenSize('8%', '4%')}
            semiBold
            style={{textAlign: 'left'}}>
            {formattedText}
          </TipText>

          <Triangle wdp={wdp} />
        </TipContainer>
      </Animated.View>
    </>
  );
};

export default React.memo(TibButton);
